import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  bankAccountsApi,
  type BankAccountDto,
  type CreateBankAccountRequest,
} from '../../api/bankAccounts';
import { formatMoney } from '../../utils/formatMoney';
import { formatDate, monthsAgo, today } from '../../utils/formatDate';
import DataTable from '../../components/DataTable';
import DatePickerInput from '../../components/DatePickerInput';
import LoadingSpinner from '../../components/LoadingSpinner';
import MoneyCell from '../../components/MoneyCell';
import { useToast } from '../../components/Toast';

type Tab = 'accounts' | 'balances' | 'statement';

export default function BankAccountsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  const [activeTab, setActiveTab] = useState<Tab>('accounts');
  const [showForm, setShowForm] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<BankAccountDto | null>(null);
  const [filterType, setFilterType] = useState<string>('');

  // Bank accounts list
  const { data: accounts, isLoading: accountsLoading } = useQuery({
    queryKey: ['bank-accounts', filterType],
    queryFn: () => bankAccountsApi.list({ accountType: filterType || undefined }),
  });

  // All balances
  const { data: balances, isLoading: balancesLoading } = useQuery({
    queryKey: ['bank-accounts-balances'],
    queryFn: () => bankAccountsApi.getAllBalances(),
  });

  // Statement for selected account
  const [stmtFrom, setStmtFrom] = useState(monthsAgo(1));
  const [stmtTo, setStmtTo] = useState(today());
  const [stmtAccountId, setStmtAccountId] = useState<number | null>(null);

  const { data: statement, isLoading: stmtLoading } = useQuery({
    queryKey: ['bank-statement', stmtAccountId, stmtFrom, stmtTo],
    queryFn: () => bankAccountsApi.getStatement(stmtAccountId!, stmtFrom, stmtTo),
    enabled: stmtAccountId !== null,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => bankAccountsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bank-accounts'] });
      addToast(t('banking.accountDeleted'), 'success');
    },
    onError: (err: Error) => addToast(`Error: ${err.message}`, 'error'),
  });

  if (accountsLoading && activeTab === 'accounts') return <LoadingSpinner />;
  if (balancesLoading && activeTab === 'balances') return <LoadingSpinner />;

  const tabs: { key: Tab; label: string }[] = [
    { key: 'accounts', label: t('banking.accounts') },
    { key: 'balances', label: t('banking.balances') },
    { key: 'statement', label: t('banking.statement') },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-900">{t('banking.bankAccounts')}</h1>
        <button
          className="px-4 py-1.5 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700"
          onClick={() => { setShowForm(true); setSelectedAccount(null); }}
        >
          {t('banking.newAccount')}
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200">
        {tabs.map(tab => (
          <button
            key={tab.key}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.key
                ? 'border-primary-600 text-primary-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Account Type Filter */}
      {activeTab === 'accounts' && (
        <div className="flex gap-2 items-end">
          <div>
            <label className="block text-xs text-gray-500 mb-1">{t('banking.accountType')}</label>
            <select
              className="px-2 py-1.5 text-sm border border-gray-300 rounded-md"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
            >
              <option value="">{t('banking.allTypes')}</option>
              <option value="Bank">{t('banking.typeBank')}</option>
              <option value="PseudoBank">{t('banking.typePseudo')}</option>
              <option value="Till">{t('banking.typeTill')}</option>
            </select>
          </div>
        </div>
      )}

      {/* Create/Edit form */}
      {showForm && (
        <BankAccountForm
          account={selectedAccount}
          onSubmit={async (data) => {
            if (selectedAccount) {
              await bankAccountsApi.update(selectedAccount.id, { ...data, isObsolete: selectedAccount.isObsolete });
              addToast(t('banking.accountUpdated'), 'success');
            } else {
              await bankAccountsApi.create(data);
              addToast(t('banking.accountCreated'), 'success');
            }
            queryClient.invalidateQueries({ queryKey: ['bank-accounts'] });
            setShowForm(false);
            setSelectedAccount(null);
          }}
          onCancel={() => { setShowForm(false); setSelectedAccount(null); }}
          t={t}
        />
      )}

      {/* Accounts tab */}
      {activeTab === 'accounts' && (
        <div className="bg-white rounded-lg border border-gray-200">
          <DataTable
            columns={[
              {
                key: 'name',
                header: t('banking.accountName'),
                className: 'font-medium',
              },
              {
                key: 'iban',
                header: 'IBAN',
                className: 'whitespace-nowrap font-mono text-xs',
              },
              {
                key: 'bankName',
                header: t('banking.bankNameCol'),
                className: 'max-w-32 truncate',
              },
              {
                key: 'currencyCode',
                header: t('common.currency'),
                className: 'text-center',
              },
              {
                key: 'accountType',
                header: t('banking.type'),
                render: (row) => (
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                    row.accountType === 'Till' ? 'bg-amber-100 text-amber-800' :
                    row.accountType === 'PseudoBank' ? 'bg-blue-100 text-blue-800' :
                    'bg-green-100 text-green-800'
                  }`}>
                    {row.accountType}
                  </span>
                ),
              },
              {
                key: 'glAccountNumber',
                header: t('banking.glAccount'),
                className: 'text-center',
              },
              {
                key: 'isObsolete',
                header: t('banking.status'),
                render: (row) => (
                  <span className={`text-xs ${row.isObsolete ? 'text-gray-400' : 'text-green-600 font-medium'}`}>
                    {row.isObsolete ? t('banking.inactive') : t('banking.active')}
                  </span>
                ),
              },
              {
                key: 'actions',
                header: '',
                render: (row) => (
                  <div className="flex gap-2 justify-end">
                    <button
                      className="text-xs text-primary-600 hover:underline"
                      onClick={(e) => { e.stopPropagation(); setSelectedAccount(row); setShowForm(true); }}
                    >
                      {t('common.edit')}
                    </button>
                    <button
                      className="text-xs text-red-500 hover:underline"
                      onClick={(e) => { e.stopPropagation(); if (confirm(t('banking.deleteAccountConfirm', `Delete account "${row.name}"?`))) deleteMutation.mutate(row.id); }}
                    >
                      {t('common.delete')}
                    </button>
                  </div>
                ),
                className: 'text-right',
              },
            ]}
            data={accounts ?? []}
            keyFn={(row) => row.id}
            emptyMessage={t('banking.noBankAccounts')}
          />
        </div>
      )}

      {/* Balances tab */}
      {activeTab === 'balances' && (
        <div className="space-y-3">
          {/* Total summary */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
              <div className="text-xs text-gray-500 mb-1">{t('banking.totalInflow')}</div>
              <div className="text-xl font-semibold text-green-600 num">
                <MoneyCell value={(balances ?? []).reduce((s, b) => s + b.totalDebit, 0)} />
              </div>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
              <div className="text-xs text-gray-500 mb-1">{t('banking.totalOutflow')}</div>
              <div className="text-xl font-semibold text-red-600 num">
                <MoneyCell value={(balances ?? []).reduce((s, b) => s + b.totalCredit, 0)} />
              </div>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
              <div className="text-xs text-gray-500 mb-1">{t('banking.totalNet')}</div>
              <div className={`text-xl font-semibold num ${
                (balances ?? []).reduce((s, b) => s + b.netBalance, 0) >= 0 ? 'text-green-600' : 'text-red-600'
              }`}>
                <MoneyCell value={(balances ?? []).reduce((s, b) => s + b.netBalance, 0)} />
              </div>
            </div>
          </div>

          {/* Per-account balances */}
          <div className="bg-white rounded-lg border border-gray-200">
            <DataTable
              columns={[
                {
                  key: 'bankAccountName',
                  header: t('banking.accountName'),
                  className: 'font-medium',
                },
                {
                  key: 'glAccountNumber',
                  header: t('banking.glAccount'),
                  className: 'text-center',
                },
                {
                  key: 'currencyCode',
                  header: t('common.currency'),
                  className: 'text-center',
                },
                {
                  key: 'totalDebit',
                  header: t('banking.debit'),
                  render: (row) => <span className="num">{formatMoney(row.totalDebit)}</span>,
                  className: 'text-right',
                },
                {
                  key: 'totalCredit',
                  header: t('banking.credit'),
                  render: (row) => <span className="num">{formatMoney(row.totalCredit)}</span>,
                  className: 'text-right',
                },
                {
                  key: 'netBalance',
                  header: t('banking.netBalance'),
                  render: (row) => (
                    <span className={`num font-semibold ${row.netBalance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatMoney(row.netBalance)}
                    </span>
                  ),
                  className: 'text-right',
                },
              ]}
              data={balances ?? []}
              keyFn={(row) => row.bankAccountId}
              emptyMessage={t('banking.noBalances')}
            />
          </div>
        </div>
      )}

      {/* Statement tab */}
      {activeTab === 'statement' && (
        <div className="space-y-3">
          <div className="flex flex-wrap gap-3 items-end">
            <div>
              <label className="block text-xs text-gray-500 mb-1">{t('banking.selectAccount')}</label>
              <select
                className="px-2 py-1.5 text-sm border border-gray-300 rounded-md min-w-[200px]"
                value={stmtAccountId ?? ''}
                onChange={(e) => setStmtAccountId(e.target.value ? parseInt(e.target.value) : null)}
              >
                <option value="">{t('banking.chooseAccount')}</option>
                {(accounts ?? []).map(a => (
                  <option key={a.id} value={a.id}>{a.name} ({a.currencyCode})</option>
                ))}
              </select>
            </div>
            <DatePickerInput
              label={t('common.from')}
              value={stmtFrom}
              onChange={setStmtFrom}
              inputClassName="w-36"
            />
            <DatePickerInput
              label={t('common.to')}
              value={stmtTo}
              onChange={setStmtTo}
              inputClassName="w-36"
            />
          </div>

          {stmtLoading && <LoadingSpinner />}

          {statement && (
            <>
              {/* Statement summary */}
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
                  <div>
                    <div className="text-xs text-gray-500">{t('banking.openingBalance')}</div>
                    <div className="text-lg font-semibold num">{formatMoney(statement.openingBalance)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">{t('banking.periodDebit')}</div>
                    <div className="text-lg font-semibold text-green-600 num">{formatMoney(statement.periodDebit)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">{t('banking.periodCredit')}</div>
                    <div className="text-lg font-semibold text-red-600 num">{formatMoney(statement.periodCredit)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">{t('banking.periodNet')}</div>
                    <div className={`text-lg font-semibold num ${statement.periodNet >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatMoney(statement.periodNet)}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">{t('banking.closingBalance')}</div>
                    <div className={`text-lg font-bold num ${statement.closingBalance >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                      {formatMoney(statement.closingBalance)}
                    </div>
                  </div>
                </div>
                <div className="mt-2 text-xs text-gray-400 text-center">
                  {statement.bankAccountName} &middot; {statement.iban} &middot; {statement.currencyCode}
                  {' '}&middot; {formatDate(statement.periodFrom)} – {formatDate(statement.periodTo)}
                </div>
              </div>

              {/* Transactions */}
              <div className="bg-white rounded-lg border border-gray-200">
                <DataTable
                  columns={[
                    {
                      key: 'date',
                      header: t('common.date'),
                      render: (row) => formatDate(row.date),
                      className: 'whitespace-nowrap',
                    },
                    {
                      key: 'documentNumber',
                      header: t('common.docNumber'),
                      className: 'whitespace-nowrap',
                    },
                    {
                      key: 'debit',
                      header: t('banking.debit'),
                      render: (row) => row.debit > 0 ? <span className="num">{formatMoney(row.debit)}</span> : <span className="text-gray-300">—</span>,
                      className: 'text-right',
                    },
                    {
                      key: 'credit',
                      header: t('banking.credit'),
                      render: (row) => row.credit > 0 ? <span className="num">{formatMoney(row.credit)}</span> : <span className="text-gray-300">—</span>,
                      className: 'text-right',
                    },
                    {
                      key: 'currencyCode',
                      header: t('common.currency'),
                      className: 'text-center',
                    },
                  ]}
                  data={statement.transactions}
                  keyFn={(row) => row.id}
                  emptyMessage={t('banking.noTransactions')}
                />
              </div>
            </>
          )}

          {!stmtAccountId && (
            <div className="text-center py-8 text-gray-400">
              {t('banking.selectAccountForStatement')}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function BankAccountForm({ account, onSubmit, onCancel, t }: {
  account: BankAccountDto | null;
  onSubmit: (data: CreateBankAccountRequest) => Promise<void>;
  onCancel: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  t: any;
}) {
  const [form, setForm] = useState<CreateBankAccountRequest>({
    name: account?.name ?? '',
    iban: account?.iban ?? '',
    bankName: account?.bankName ?? '',
    bankCode: account?.bankCode ?? '',
    currencyCode: account?.currencyCode ?? 'EUR',
    accountType: account?.accountType === 'PseudoBank' ? 1 : account?.accountType === 'Till' ? 2 : 0,
    glAccountNumber: account?.glAccountNumber ?? 0,
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    setSaving(true);
    try {
      await onSubmit(form);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error');
    }
    setSaving(false);
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <h2 className="font-semibold text-gray-900 mb-3">
        {account ? t('banking.editAccount') : t('banking.newAccount')}
      </h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div>
          <label className="block text-xs text-gray-500 mb-1">{t('banking.accountNameLabel')} *</label>
          <input
            type="text"
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="e.g. Main EUR Account"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">IBAN</label>
          <input
            type="text"
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md font-mono"
            value={form.iban}
            onChange={(e) => setForm({ ...form, iban: e.target.value })}
            placeholder="LT12 3456 7890 1234 5678"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">{t('banking.bankNameLabel')}</label>
          <input
            type="text"
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
            value={form.bankName}
            onChange={(e) => setForm({ ...form, bankName: e.target.value })}
            placeholder="e.g. Swedbank"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">{t('banking.bankCodeLabel')}</label>
          <input
            type="text"
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md font-mono"
            value={form.bankCode}
            onChange={(e) => setForm({ ...form, bankCode: e.target.value })}
            placeholder="e.g. 73000"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">{t('banking.currencyLabel')}</label>
          <select
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
            value={form.currencyCode ?? 'EUR'}
            onChange={(e) => setForm({ ...form, currencyCode: e.target.value })}
          >
            <option value="EUR">EUR</option>
            <option value="USD">USD</option>
            <option value="GBP">GBP</option>
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">{t('banking.typeLabel')}</label>
          <select
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
            value={form.accountType}
            onChange={(e) => setForm({ ...form, accountType: parseInt(e.target.value) })}
          >
            <option value={0}>{t('banking.typeBank')}</option>
            <option value={1}>{t('banking.typePseudo')}</option>
            <option value={2}>{t('banking.typeTill')}</option>
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">{t('banking.glAccountLabel')} *</label>
          <input
            type="number"
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
            value={form.glAccountNumber || ''}
            onChange={(e) => setForm({ ...form, glAccountNumber: parseInt(e.target.value) || 0 })}
            placeholder="e.g. 2410"
          />
        </div>
        <div className="flex items-end gap-2">
          <button
            className="px-4 py-1.5 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
            onClick={handleSubmit}
            disabled={saving || !form.name.trim() || form.glAccountNumber <= 0}
          >
            {saving ? t('common.saving') : (account ? t('common.update') : t('common.create'))}
          </button>
          <button
            className="px-4 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
            onClick={onCancel}
          >
            {t('common.cancel')}
          </button>
        </div>
      </div>
    </div>
  );
}
